/**
 * App slice - manages global app state including user mode, age, and loading
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type { UserMode, AgeRange } from "../../types/landing";

// Re-export types for convenience
export type { UserMode, AgeRange };

interface AppState {
  isLoading: boolean;
  userMode: UserMode | null;
  ageRange: AgeRange | null;
  hasConsented: boolean;
  isLightMode: boolean;
}

const initialState: AppState = {
  isLoading: false,
  userMode: null,
  ageRange: null,
  hasConsented: false,
  isLightMode: false, // Default to dark mode
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUserMode: (state, action: PayloadAction<UserMode | null>) => {
      state.userMode = action.payload;
      // Reset age and consent when mode changes from explorer
      if (action.payload !== "explorer") {
        state.ageRange = null;
        state.hasConsented = false;
      }
    },
    setAgeRange: (state, action: PayloadAction<AgeRange | null>) => {
      state.ageRange = action.payload;
    },
    setHasConsented: (state, action: PayloadAction<boolean>) => {
      state.hasConsented = action.payload;
    },
    setLightMode: (state, action: PayloadAction<boolean>) => {
      state.isLightMode = action.payload;
    },
    toggleLightMode: state => {
      state.isLightMode = !state.isLightMode;
    },
    resetAppState: state => {
      state.userMode = null;
      state.ageRange = null;
      state.hasConsented = false;
      // Don't reset light mode - user preference persists
    },
  },
});

// Actions
export const {
  setLoading,
  setUserMode,
  setAgeRange,
  setHasConsented,
  setLightMode,
  toggleLightMode,
  resetAppState,
} = appSlice.actions;

// Selectors - use these instead of directly accessing state
export const selectIsLoading = (state: RootState) => state.app.isLoading;
export const selectUserMode = (state: RootState) => state.app.userMode;
export const selectAgeRange = (state: RootState) => state.app.ageRange;
export const selectHasConsented = (state: RootState) => state.app.hasConsented;
export const selectIsLightMode = (state: RootState) => state.app.isLightMode;

// Derived selectors - computed values
export const selectIsExplorer = (state: RootState) => state.app.userMode === "explorer";
export const selectIsAdmin = (state: RootState) => state.app.userMode === "admin";
export const selectIsAdult = (state: RootState) =>
  state.app.userMode === "adult" || state.app.userMode === null;

export const selectCanContinue = (state: RootState) =>
  state.app.userMode === "explorer" && state.app.ageRange !== null && state.app.hasConsented;

export const selectAppState = (state: RootState) => state.app;

export default appSlice.reducer;
